// frontend/app/components/lessonPlans/authoring/WizardFields.js
//
// Form primitives shared by the authoring steps. Kept here so each step stays
// about lesson structure rather than re-deriving input chrome, and so every
// control picks up dark mode from the same tokens.

import React from "react";
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

export const Field = ({ colors, label, hint, error, required, children }) => (
  <View style={styles.field}>
    {label ? (
      <Text style={[styles.label, { color: colors.textSecondary }]}>
        {label}
        {required ? <Text style={{ color: colors.danger }}> *</Text> : null}
      </Text>
    ) : null}
    {children}
    {error ? (
      <Text style={[styles.error, { color: colors.danger }]}>{error}</Text>
    ) : hint ? (
      <Text style={[styles.hint, { color: colors.textMuted }]}>{hint}</Text>
    ) : null}
  </View>
);

export const TextField = ({
  colors,
  value,
  onChange,
  placeholder,
  multiline = false,
  keyboardType,
  error,
  accessibilityLabel,
}) => (
  <TextInput
    value={value === null || value === undefined ? "" : String(value)}
    onChangeText={onChange}
    placeholder={placeholder}
    placeholderTextColor={colors.textMuted}
    multiline={multiline}
    keyboardType={keyboardType}
    accessibilityLabel={accessibilityLabel || placeholder}
    style={[
      styles.input,
      multiline && styles.inputMultiline,
      {
        backgroundColor: colors.subtleBg,
        borderColor: error ? colors.danger : colors.border,
        color: colors.textPrimary,
      },
    ]}
  />
);

/** Single-choice chips — phase, instructional mode, difficulty, status. */
export const ChipSelect = ({ colors, options, value, onChange, accessibilityLabel }) => (
  <View style={styles.chipRow} accessibilityLabel={accessibilityLabel}>
    {options.map((option) => {
      const selected = option.value === value;
      const accent = option.accent || colors.accentPrimary;
      return (
        <TouchableOpacity
          key={option.value}
          onPress={() => onChange(option.value)}
          accessibilityRole="button"
          accessibilityState={{ selected }}
          accessibilityLabel={option.label}
          style={[
            styles.chip,
            {
              backgroundColor: selected ? `${accent}22` : colors.subtleBg,
              borderColor: selected ? accent : colors.border,
            },
          ]}
        >
          <Text
            style={[styles.chipText, { color: selected ? accent : colors.textSecondary }]}
          >
            {option.label}
          </Text>
        </TouchableOpacity>
      );
    })}
  </View>
);

export const IconButton = ({ colors, icon, label, onPress, disabled, danger }) => (
  <TouchableOpacity
    onPress={onPress}
    disabled={disabled}
    accessibilityRole="button"
    accessibilityLabel={label}
    style={[
      styles.iconButton,
      {
        borderColor: colors.border,
        backgroundColor: colors.panelBg,
        opacity: disabled ? 0.35 : 1,
      },
    ]}
  >
    <MaterialCommunityIcons
      name={icon}
      size={15}
      color={danger ? colors.danger : colors.textSecondary}
    />
  </TouchableOpacity>
);

/**
 * An ordered list of plain strings. Objectives, prerequisites, materials and
 * teacher tips are all this same shape, so they share one control.
 */
export const StringListField = ({
  colors,
  items = [],
  onChange,
  placeholder,
  addLabel = "Add",
  ordered = false,
  t,
}) => {
  const translate = t || ((value) => value);

  const update = (index, next) =>
    onChange(items.map((item, i) => (i === index ? next : item)));
  const remove = (index) => onChange(items.filter((_, i) => i !== index));
  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    next[index] = items[target];
    next[target] = items[index];
    onChange(next);
  };

  return (
    <View>
      {items.map((item, index) => (
        <View key={`row-${index}`} style={styles.listRow}>
          {ordered ? (
            <View style={[styles.listIndex, { backgroundColor: colors.accentGlow }]}>
              <Text style={[styles.listIndexText, { color: colors.accentPrimary }]}>
                {index + 1}
              </Text>
            </View>
          ) : null}

          <TextInput
            value={item}
            onChangeText={(next) => update(index, next)}
            placeholder={placeholder}
            placeholderTextColor={colors.textMuted}
            multiline
            accessibilityLabel={`${placeholder} ${index + 1}`}
            style={[
              styles.input,
              styles.listInput,
              {
                backgroundColor: colors.subtleBg,
                borderColor: colors.border,
                color: colors.textPrimary,
              },
            ]}
          />

          <View style={styles.listActions}>
            {ordered ? (
              <>
                <IconButton
                  colors={colors}
                  icon="chevron-up"
                  label={translate("Move up")}
                  disabled={index === 0}
                  onPress={() => move(index, -1)}
                />
                <IconButton
                  colors={colors}
                  icon="chevron-down"
                  label={translate("Move down")}
                  disabled={index === items.length - 1}
                  onPress={() => move(index, 1)}
                />
              </>
            ) : null}
            <IconButton
              colors={colors}
              icon="close"
              label={`${translate("Remove")} ${index + 1}`}
              danger
              onPress={() => remove(index)}
            />
          </View>
        </View>
      ))}

      <TouchableOpacity
        onPress={() => onChange([...items, ""])}
        accessibilityRole="button"
        accessibilityLabel={translate(addLabel)}
        style={[styles.addButton, { borderColor: colors.borderStrong }]}
      >
        <MaterialCommunityIcons name="plus" size={15} color={colors.accentPrimary} />
        <Text style={[styles.addText, { color: colors.accentPrimary }]}>
          {translate(addLabel)}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

export const SectionCard = ({ colors, title, subtitle, icon, accent, right, children }) => (
  <View
    style={[styles.card, { backgroundColor: colors.panelBg, borderColor: colors.border }]}
  >
    {title ? (
      <View style={styles.cardHeader}>
        {icon ? (
          <MaterialCommunityIcons
            name={icon}
            size={16}
            color={accent || colors.accentPrimary}
          />
        ) : null}
        <View style={{ flex: 1 }}>
          <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>{title}</Text>
          {subtitle ? (
            <Text style={[styles.cardSubtitle, { color: colors.textMuted }]}>
              {subtitle}
            </Text>
          ) : null}
        </View>
        {right || null}
      </View>
    ) : null}
    {children}
  </View>
);

const styles = StyleSheet.create({
  field: { marginBottom: 14 },
  label: {
    fontSize: 11,
    fontWeight: "800",
    letterSpacing: 0.5,
    textTransform: "uppercase",
    marginBottom: 6,
  },
  hint: { fontSize: 11, marginTop: 5 },
  error: { fontSize: 11, marginTop: 5, fontWeight: "600" },

  input: {
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
  },
  inputMultiline: { minHeight: 78, textAlignVertical: "top", lineHeight: 20 },

  chipRow: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  chip: {
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  chipText: { fontSize: 12, fontWeight: "700" },

  listRow: { flexDirection: "row", alignItems: "flex-start", marginBottom: 8, gap: 8 },
  listInput: { flex: 1, minHeight: 40 },
  listIndex: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 9,
  },
  listIndexText: { fontSize: 11, fontWeight: "800" },
  listActions: { flexDirection: "row", gap: 4, marginTop: 4 },

  iconButton: {
    width: 28,
    height: 28,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },

  addButton: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    borderRadius: 9,
    borderWidth: 1,
    borderStyle: "dashed",
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 5,
    marginTop: 2,
  },
  addText: { fontSize: 12, fontWeight: "700" },

  card: { borderRadius: 14, borderWidth: 1, padding: 16, marginBottom: 14 },
  cardHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 9,
    marginBottom: 12,
  },
  cardTitle: { fontSize: 14, fontWeight: "700" },
  cardSubtitle: { fontSize: 12, marginTop: 2 },
});
